import { Tag } from "lucide-react";
import type { ProjectDataType } from "store/types/project";
import { Button } from "components/common/buttons/Button";
import { FileUploadSection } from "components/common/CreateProjectSheet/components/FileUploadSection";
import {
  finalDraftHeldOffFeaturesNote,
  finalDraftTagsFoundMessage,
  type HeldOffFeatures,
  type ParsedFinalDraftTags,
} from "services/finalDraftTags";

export type ImportStageProps = {
  file: File | null;
  fileLoading: boolean;
  fileError: string | null;
  handleFileUpload: (
    acceptedFiles: File[],
    fileRejections: { errors: Array<{ message: string }> }[],
    mode?: string,
    existingProject?: unknown
  ) => void;
  mode: "project" | "version";
  existingProject?: Partial<ProjectDataType>;
  /** Offered while no screenplay is attached, so users without a script are not stuck. */
  onStartBlank?: () => void;
  /** Final Draft tags found in the attached file. */
  finalDraftTags?: ParsedFinalDraftTags;
  /** Features that are off only because Script breakdown starts off for this file. */
  heldOffFeatures?: HeldOffFeatures | null;
};

export const ImportStage = ({
  file,
  fileLoading,
  fileError,
  handleFileUpload,
  mode,
  existingProject,
  onStartBlank,
  finalDraftTags,
  heldOffFeatures = null,
}: ImportStageProps) => {
  const heldOffNote = heldOffFeatures ? finalDraftHeldOffFeaturesNote(heldOffFeatures) : null;
  return (
    <div className="flex flex-col gap-4">
      <FileUploadSection
        file={file}
        fileLoading={fileLoading}
        fileError={fileError}
        handleFileUpload={handleFileUpload}
        mode={mode}
        existingProject={existingProject}
      />
      {file && !fileLoading && finalDraftTags && finalDraftTags.source > 0 ? (
        <div className="flex flex-col gap-1">
          <p role="status" className="flex items-center gap-2 text-sm text-foreground">
            <Tag className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            {finalDraftTagsFoundMessage(finalDraftTags)}
          </p>
          {heldOffNote ? (
            <p
              className="pl-6 text-xs text-muted-foreground"
              data-testid="final-draft-dependent-features-note"
            >
              {heldOffNote}
            </p>
          ) : null}
        </div>
      ) : null}
      {onStartBlank && !file && !fileLoading ? (
        <p className="text-sm text-muted-foreground">
          No screenplay yet?{" "}
          <Button type="button" size="xs" onClick={onStartBlank}>
            Start with a blank project
          </Button>{" "}
          and write it in RivetAI.
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Your script stays private and is never used to train AI.
      </p>
    </div>
  );
};
